/**
 * Apply Potree clip-box volumes to reality-mesh (3D Tiles) materials.
 *
 * Point clouds use PointCloudMaterial.setClipBoxes + pointcloud.vs.
 * Panorama markers use a CPU AABB test in Images360.
 * Reality meshes are standard Three.js materials (often from a host
 * TilesRenderer), so we inject the same inverse-matrix / ClipTask /
 * ClipMethod logic via material.onBeforeCompile.
 *
 * ClipTask parity with pointcloud.vs:
 * - NONE / HIGHLIGHT → full mesh (Highlight tints inside; no discard)
 * - SHOW_INSIDE / SHOW_OUTSIDE → geometric clip via discard
 * ClipMethod INSIDE_ANY / INSIDE_ALL matches the cloud shader.
 *
 * @see https://github.com/GeoMarientall/noach/issues/700
 */

import * as THREE from '../../libs/three.js/build/three.module.js';
import { ClipTask, ClipMethod } from '../defines.js';
import { BoxVolume } from './Volume.js';

/** Fixed GLSL array size — enough for typical clip + floor volumes. */
export const MAX_REALITY_MESH_CLIP_BOXES = 8;

const MATERIAL_FLAG = '__potreeRealityClipHooked';

/**
 * Active clip-box inverse matrices — same sources as Viewer.update / Images360.
 * @param {import('../viewer/viewer.js').Viewer} viewer
 * @returns {THREE.Matrix4[]}
 */
export function collectClipBoxInverses(viewer) {
  const scene = viewer?.scene;
  if (!scene) return [];

  const boxes = [];
  if (scene.volumes) {
    for (const volume of scene.volumes) {
      if (volume.clip && volume instanceof BoxVolume) {
        boxes.push(volume);
      }
    }
  }
  if (scene.profiles) {
    for (const profile of scene.profiles) {
      if (profile.boxes) {
        boxes.push(...profile.boxes);
      }
    }
  }

  const inverses = [];
  for (const box of boxes) {
    box.updateMatrixWorld();
    if (box.matrixWorld.determinant() === 0) {
      continue;
    }
    inverses.push(box.matrixWorld.clone().invert());
  }
  return inverses;
}

function createSharedUniforms() {
  const identityBoxes = Array.from(
    { length: MAX_REALITY_MESH_CLIP_BOXES },
    () => new THREE.Matrix4(),
  );
  return {
    uNoachClipBoxes: { value: identityBoxes },
    uNoachClipCount: { value: 0 },
    uNoachClipTask: { value: ClipTask.NONE },
    uNoachClipMethod: { value: ClipMethod.INSIDE_ANY },
  };
}

function buildClipShaderPreamble() {
  return /* glsl */ `
uniform mat4 uNoachClipBoxes[${MAX_REALITY_MESH_CLIP_BOXES}];
uniform int uNoachClipCount;
uniform int uNoachClipTask;
uniform int uNoachClipMethod;
varying vec3 vNoachClipViewPos;
`;
}

/**
 * Early fragment discard for SHOW_INSIDE / SHOW_OUTSIDE only.
 * HIGHLIGHT is handled later (tint) — same as pointcloud.vs (no discard).
 */
function buildClipDiscardSnippet() {
  return /* glsl */ `
{
  // ClipTask: 0=NONE 1=HIGHLIGHT 2=SHOW_INSIDE 3=SHOW_OUTSIDE
  if (uNoachClipCount > 0 && (uNoachClipTask == 2 || uNoachClipTask == 3)) {
    int insideCount = 0;
    for (int i = 0; i < ${MAX_REALITY_MESH_CLIP_BOXES}; i++) {
      if (i < uNoachClipCount) {
        vec4 clipLocal = uNoachClipBoxes[i] * vec4(vNoachClipViewPos, 1.0);
        bool inside = abs(clipLocal.x) <= 0.5
          && abs(clipLocal.y) <= 0.5
          && abs(clipLocal.z) <= 0.5;
        if (inside) {
          insideCount++;
        }
      }
    }
    bool insideAny = insideCount > 0;
    bool insideAll = insideCount == uNoachClipCount;
    // ClipMethod: 0=INSIDE_ANY 1=INSIDE_ALL
    bool regionMatch = (uNoachClipMethod == 0) ? insideAny : insideAll;
    bool pass = (uNoachClipTask == 2) ? regionMatch : !regionMatch;
    if (!pass) {
      discard;
    }
  }
}
`;
}

/**
 * HIGHLIGHT tint (pointcloud.vs mixes toward clip box color).
 * Inserted after map/color so diffuseColor exists.
 */
function buildClipHighlightSnippet() {
  return /* glsl */ `
{
  if (uNoachClipCount > 0 && uNoachClipTask == 1) {
    int insideCount = 0;
    for (int i = 0; i < ${MAX_REALITY_MESH_CLIP_BOXES}; i++) {
      if (i < uNoachClipCount) {
        vec4 clipLocal = uNoachClipBoxes[i] * vec4(vNoachClipViewPos, 1.0);
        bool inside = abs(clipLocal.x) <= 0.5
          && abs(clipLocal.y) <= 0.5
          && abs(clipLocal.z) <= 0.5;
        if (inside) {
          insideCount++;
        }
      }
    }
    bool insideAny = insideCount > 0;
    bool insideAll = insideCount == uNoachClipCount;
    bool regionMatch = (uNoachClipMethod == 0) ? insideAny : insideAll;
    if (regionMatch) {
      diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0, 0.0, 0.0), 0.45);
    }
  }
}
`;
}

function hookMaterial(material, shared) {
  if (!material || material[MATERIAL_FLAG]) {
    return;
  }

  const prevOnBeforeCompile = material.onBeforeCompile
    ? material.onBeforeCompile.bind(material)
    : null;
  const prevCacheKey =
    typeof material.customProgramCacheKey === 'function'
      ? material.customProgramCacheKey.bind(material)
      : () => '';

  material.onBeforeCompile = (shader, renderer) => {
    if (prevOnBeforeCompile) {
      prevOnBeforeCompile(shader, renderer);
    }

    shader.uniforms.uNoachClipBoxes = shared.uNoachClipBoxes;
    shader.uniforms.uNoachClipCount = shared.uNoachClipCount;
    shader.uniforms.uNoachClipTask = shared.uNoachClipTask;
    shader.uniforms.uNoachClipMethod = shared.uNoachClipMethod;

    shader.vertexShader = shader.vertexShader.replace(
      'void main() {',
      `${buildClipShaderPreamble()}
void main() {`,
    );

    // Eye-space position for the clip test — never world space. Georeferenced
    // scenes sit at absolute CS2000 coordinates (~6.5e6 m) where a float32
    // varying quantises to ~0.5 m, which shreds the clip surface per fragment.
    // The box matrices are pre-multiplied by camera.matrixWorld on the CPU
    // (float64), so everything the GPU sees stays camera-relative and small.
    //
    // 1) Fragments LodMaterial — `first` / `last` are already modelViewMatrix
    //    products; `position` there is billboard-local and must not be used.
    // 2) Standard three materials — project_vertex leaves `mvPosition` in
    //    scope, already covering instancing / skinning / morph targets.
    // 3) Generic fallback — last gl_Position write (never the first, so that
    //    early-outs don't steal the injection site).
    if (shader.vertexShader.includes('itemFirst') && shader.vertexShader.includes('lodPosition')) {
      shader.vertexShader = shader.vertexShader.replace(
        'gl_Position = lodPosition;',
        /* glsl */ `gl_Position = lodPosition;
vNoachClipViewPos = (isFirst ? first : last).xyz;`,
      );
    } else if (shader.vertexShader.includes('#include <project_vertex>')) {
      shader.vertexShader = shader.vertexShader.replace(
        '#include <project_vertex>',
        /* glsl */ `#include <project_vertex>
vNoachClipViewPos = mvPosition.xyz;`,
      );
    } else {
      let lastIndex = -1;
      const re = /gl_Position\s*=\s*[^;]+;/g;
      let m;
      while ((m = re.exec(shader.vertexShader)) !== null) {
        lastIndex = m.index;
      }
      if (lastIndex >= 0) {
        const before = shader.vertexShader.slice(0, lastIndex);
        const rest = shader.vertexShader.slice(lastIndex);
        const replaced = rest.replace(
          /gl_Position\s*=\s*[^;]+;/,
          (match) =>
            `${match}
vNoachClipViewPos = (modelViewMatrix * vec4(position, 1.0)).xyz;`,
        );
        shader.vertexShader = before + replaced;
      }
    }

    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `${buildClipShaderPreamble()}
void main() {`,
    );

    if (shader.fragmentShader.includes('#include <clipping_planes_fragment>')) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <clipping_planes_fragment>',
        `#include <clipping_planes_fragment>
${buildClipDiscardSnippet()}`,
      );
    } else {
      shader.fragmentShader = shader.fragmentShader.replace(
        'void main() {',
        `void main() {
${buildClipDiscardSnippet()}`,
      );
    }

    if (shader.fragmentShader.includes('#include <color_fragment>')) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <color_fragment>',
        `#include <color_fragment>
${buildClipHighlightSnippet()}`,
      );
    } else if (shader.fragmentShader.includes('#include <map_fragment>')) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
${buildClipHighlightSnippet()}`,
      );
    }
    // LodMaterial has no diffuseColor — skip highlight tint; discard path above
    // already handles SHOW_INSIDE / SHOW_OUTSIDE via clipping_planes_fragment.
  };

  material.customProgramCacheKey = () =>
    `${prevCacheKey()}|potree-reality-clip-v4-${MAX_REALITY_MESH_CLIP_BOXES}`;

  material[MATERIAL_FLAG] = true;
  material.needsUpdate = true;
}

function forEachMaterial(root, visit) {
  if (!root || typeof root.traverse !== 'function') {
    return;
  }
  root.traverse((obj) => {
    if (!obj.isMesh && !obj.isSkinnedMesh) {
      return;
    }
    const { material } = obj;
    if (!material) {
      return;
    }
    if (Array.isArray(material)) {
      for (const m of material) {
        if (m) visit(m);
      }
    } else {
      visit(material);
    }
  });
}

/**
 * Controller that shares clip uniforms across all hooked reality-mesh materials.
 * Typically owned by Viewer and driven from Viewer.update().
 */
export function createRealityMeshClipController() {
  const shared = createSharedUniforms();
  let materialScanRequested = true;
  let frameCounter = 0;
  let disposed = false;

  // Periodic rescan catches materials created when new 3D Tiles LODs load.
  const PERIODIC_SCAN_INTERVAL = 45;

  const updateUniforms = (viewer) => {
    // The shaders feed eye-space positions into these matrices, so fold the
    // camera pose in here (JS math is float64 and the result is
    // camera-relative — no large magnitudes reach the float32 uniforms).
    const camera = viewer?.scene?.getActiveCamera?.() ?? null;
    if (camera) {
      camera.updateMatrixWorld();
    }

    const inverses = camera ? collectClipBoxInverses(viewer) : [];
    const count = Math.min(inverses.length, MAX_REALITY_MESH_CLIP_BOXES);
    const boxes = shared.uNoachClipBoxes.value;
    for (let i = 0; i < count; i++) {
      boxes[i].multiplyMatrices(inverses[i], camera.matrixWorld);
    }
    for (let i = count; i < MAX_REALITY_MESH_CLIP_BOXES; i++) {
      boxes[i].identity();
    }
    shared.uNoachClipCount.value = count;

    const task =
      typeof viewer.getClipTask === 'function'
        ? viewer.getClipTask()
        : (viewer.clipTask ?? ClipTask.NONE);
    const method =
      typeof viewer.getClipMethod === 'function'
        ? viewer.getClipMethod()
        : (viewer.clipMethod ?? ClipMethod.INSIDE_ANY);
    shared.uNoachClipTask.value = task;
    shared.uNoachClipMethod.value = method;
  };

  const scanMaterials = (roots) => {
    for (const root of roots) {
      forEachMaterial(root, (material) => {
        hookMaterial(material, shared);
      });
    }
  };

  return {
    requestMaterialScan() {
      materialScanRequested = true;
    },

    /**
     * @param {object} viewer
     * @param {THREE.Object3D[]} roots  typically viewer.scene.realityMeshes
     */
    sync(viewer, roots) {
      if (disposed || !roots || roots.length === 0) {
        return;
      }

      updateUniforms(viewer);

      frameCounter += 1;
      const shouldScan =
        materialScanRequested || frameCounter % PERIODIC_SCAN_INTERVAL === 0;
      if (shouldScan) {
        materialScanRequested = false;
        scanMaterials(roots);
      }
    },

    dispose() {
      disposed = true;
      shared.uNoachClipCount.value = 0;
      shared.uNoachClipTask.value = ClipTask.NONE;
    },
  };
}
